import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';

// ─── Toast component ───────────────────────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg =
    type === 'success' ?'bg-green-50 border-green-200 text-green-700' :'bg-red-50 border-red-200 text-red-700';

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg text-sm font-medium ${bg}`}
    >
      {type === 'success' ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )}
      {message}
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const AdminPagamentiRateali = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();

  const isAdminOrSuperadmin =
    userProfile?.role === 'admin' || userProfile?.role === 'superadmin';

  // Toggle state
  const [installmentsEnabled, setInstallmentsEnabled] = useState(false);
  const [installmentsLoading, setInstallmentsLoading] = useState(true);
  const [installmentsSaving, setInstallmentsSaving] = useState(false);

  // Text fields state
  const [installmentsTitle, setInstallmentsTitle] = useState('');
  const [installmentsSubtitle, setInstallmentsSubtitle] = useState('');
  const [installmentsTextSaving, setInstallmentsTextSaving] = useState(false);
  const [installmentsTextSuccess, setInstallmentsTextSuccess] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  // Auth guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login-form'); return; }
    if (!isAdminOrSuperadmin) { navigate('/admin'); return; }
  }, [user, authLoading, isAdminOrSuperadmin, navigate]);

  // Load platform_config on mount
  useEffect(() => {
    if (authLoading || !isAdminOrSuperadmin) return;

    const fetchConfig = async () => {
      setInstallmentsLoading(true);
      try {
        const [enabledData, textData] = await Promise.all([
          supabase
            ?.from('platform_config')
            ?.select('value')
            ?.eq('key', 'installments_enabled')
            ?.single(),
          supabase
            ?.from('platform_config')
            ?.select('value')
            ?.eq('key', 'installments_client_text')
            ?.single(),
        ]);

        if (!enabledData.error && enabledData.data) {
          setInstallmentsEnabled(enabledData.data?.value === true);
        }
        if (!textData.error && textData.data?.value) {
          setInstallmentsTitle(textData.data?.value?.title || '');
          setInstallmentsSubtitle(textData.data?.value?.subtitle || '');
        }
      } catch (err) {
        console.error('Error loading installments config:', err);
      } finally {
        setInstallmentsLoading(false);
      }
    };

    fetchConfig();
  }, [authLoading, isAdminOrSuperadmin]);

  // ── Toggle handler ────────────────────────────────────────────────────────────
  const handleInstallmentsToggle = async () => {
    const newValue = !installmentsEnabled;
    setInstallmentsSaving(true);
    try {
      const {
        data: { user: freshUser },
        error: authErr,
      } = await supabase?.auth?.getUser();
      if (authErr || !freshUser) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('platform_config')
        ?.update({
          value: newValue,
          updated_by: freshUser?.id,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('key', 'installments_enabled');

      if (error) throw error;
      setInstallmentsEnabled(newValue);
      showToast(newValue ? 'Pagamenti rateali attivati' : 'Pagamenti rateali disattivati');
    } catch (err) {
      console.error('Error updating installments toggle:', err);
      showToast(err?.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setInstallmentsSaving(false);
    }
  };

  // ── Text save handler ─────────────────────────────────────────────────────────
  const handleInstallmentsTextSave = async () => {
    setInstallmentsTextSaving(true);
    setInstallmentsTextSuccess(false);
    try {
      const {
        data: { user: freshUser },
        error: authErr,
      } = await supabase?.auth?.getUser();
      if (authErr || !freshUser) throw new Error('Not authenticated');

      const { error } = await supabase
        ?.from('platform_config')
        ?.update({
          value: { title: installmentsTitle, subtitle: installmentsSubtitle },
          updated_by: freshUser?.id,
          updated_at: new Date()?.toISOString(),
        })
        ?.eq('key', 'installments_client_text');

      if (error) throw error;
      setInstallmentsTextSuccess(true);
      showToast('Testo salvato con successo');
      setTimeout(() => setInstallmentsTextSuccess(false), 3500);
    } catch (err) {
      console.error('Error saving installments text:', err);
      showToast(err?.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setInstallmentsTextSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminDashboardLayout>
      <div className="max-w-[720px] mx-auto px-4 py-10">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Pagamenti rateali</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestisci la funzionalità di pagamento rateale per i clienti della piattaforma.
          </p>
        </div>

        {/* ── Card ─────────────────────────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Pagamenti rateali</h2>
            <p className="text-sm text-gray-500 mb-5">
              Abilita o disabilita la funzionalità di pagamento rateale e personalizza il testo mostrato ai clienti.
            </p>

            {installmentsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                Caricamento...
              </div>
            ) : (
              <>
                {/* Toggle row */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 bg-gray-50 mb-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Attiva pagamenti rateali</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {installmentsEnabled ? 'Attivo' : 'Disattivo (nascosto)'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleInstallmentsToggle}
                    disabled={installmentsSaving}
                    aria-pressed={installmentsEnabled}
                    className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      installmentsEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        installmentsEnabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Client text fields */}
                <div className="border-t border-gray-100 pt-5">
                  <h3 className="text-sm font-semibold text-gray-800 mb-4">Testo mostrato ai clienti</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
                      <input
                        type="text"
                        value={installmentsTitle}
                        onChange={(e) => setInstallmentsTitle(e?.target?.value)}
                        placeholder="Es. Pagamento rateale disponibile"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sottotitolo</label>
                      <input
                        type="text"
                        value={installmentsSubtitle}
                        onChange={(e) => setInstallmentsSubtitle(e?.target?.value)}
                        placeholder="Es. Indica ai professionisti che sei interessato a un pagamento a rate."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    {installmentsTextSuccess && (
                      <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                        Testo salvato con successo ✓
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleInstallmentsTextSave}
                      disabled={installmentsTextSaving}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                      style={{ backgroundColor: '#104d98' }}
                    >
                      {installmentsTextSaving ? 'Salvataggio...' : 'Salva'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {/* Toast */}
      {toast && (
        <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      )}
    </AdminDashboardLayout>
  );
};

export default AdminPagamentiRateali;
